import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { CAMERA_YAW_OFFSET } from '../services/seats';
import { seatMotion } from '../state/seatMotion';
import { cutsceneBridge } from '../city/cutscene';

// SeatCamera — the camera as the player's body.
//
// First person has no body yet, so the camera IS the player: moving it is how
// sitting down is shown. That is a rendering decision, not a design one, which
// is why the state it reads is `seated` — a pose the PLAYER holds — rather than
// anything about a camera. When a body arrives it subscribes to the same value
// and this keeps doing exactly what it does now.
//
// ── WHY IT TURNS FIRST, THEN SITS ────────────────────────────────────────────
// Because that is the order a person does it in, and doing both at once reads as
// sliding. The yaw finishes in the first 45% of the move while the position has
// barely started; then the body settles into the chair. Sitting is not a
// straight line either — you drop as you arrive, so height eases on a different
// curve from the ground position.
const SIT_MS = 1250;
const STAND_MS = 900;

// Shortest way round. Turning 350° to your left because the numbers said so is
// the single most obvious tell that a camera is being animated rather than a
// person turning.
const shortest = (from, to) => from + Math.atan2(Math.sin(to - from), Math.cos(to - from));

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);
const easeOut = (t) => 1 - (1 - t) ** 3;

export default function SeatCamera() {
  const camera = useThree((s) => s.camera);
  const seated = useWorldStore((s) => s.seated);
  const seatSettled = useWorldStore((s) => s.seatSettled);
  const standCleared = useWorldStore((s) => s.standCleared);

  const run = useRef(null);   // { phase, t, from, to }
  const euler = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));
  const a = useRef(new THREE.Vector3());
  const b = useRef(new THREE.Vector3());

  useFrame((_, dt) => {
    if (!seated) { run.current = null; return; }
    const phase = seated.phase;

    // Settled: hold the seat. Position only — look is still yours, so you can
    // glance around from the chair, which is most of what makes it feel like
    // being somewhere rather than being parked.
    if (phase === 'seated') {
      // A chat scene has the camera (sat across from an artist at a café table,
      // components/ChatSceneCamera): it hands the seat's view back when it ends.
      if (cutsceneBridge.active) { run.current = null; return; }
      camera.position.set(seated.seat.pos[0], seated.seat.pos[1], seated.seat.pos[2]);
      // A held pose that moves a little (state/seatMotion): the stride of
      // running on a treadmill (GymFloor). Added to the hold, never accumulated.
      const o = seatMotion.fn?.(performance.now() / 1000, seated.target);
      if (o) { camera.position.x += o[0]; camera.position.y += o[1]; camera.position.z += o[2]; }
      run.current = null;
      return;
    }

    // Start (or restart) a transition.
    if (!run.current || run.current.phase !== phase) {
      const to = phase === 'sitting' ? seated.seat : seated.from;
      const from = phase === 'sitting' ? seated.from : seated.seat;
      run.current = {
        phase,
        t: 0,
        ms: phase === 'sitting' ? SIT_MS : STAND_MS,
        from,
        to: { ...to, yaw: shortest(from.yaw, to.yaw) },
      };
    }

    const r = run.current;
    r.t = Math.min(1, r.t + (dt * 1000) / r.ms);

    // TURN FIRST. Yaw completes in the first 45% of the move; the walk into the
    // chair happens under it.
    const kTurn = easeInOut(Math.min(1, r.t / 0.45));
    // The ground move lags the turn and lands early.
    const kMove = easeInOut(Math.max(0, (r.t - 0.15) / 0.85));
    // Height eases out on its own curve — you drop into a chair, you do not
    // descend into it linearly.
    const kDrop = easeOut(Math.max(0, (r.t - 0.3) / 0.7));

    a.current.set(r.from.pos[0], 0, r.from.pos[2]);
    b.current.set(r.to.pos[0], 0, r.to.pos[2]);
    a.current.lerp(b.current, kMove);
    camera.position.x = a.current.x;
    camera.position.z = a.current.z;
    camera.position.y = THREE.MathUtils.lerp(r.from.pos[1], r.to.pos[1], kDrop);

    euler.current.set(
      THREE.MathUtils.lerp(r.from.pitch || 0, r.to.pitch || 0, kDrop),
      // + π converts FACING to CAMERA. A pose says which way the player is
      // looking (0 = +z, like everything else in the scene); a camera looks down
      // its own −z. Applied here, at the last moment, so the pose stays a fact
      // about the player and the body can read it later without undoing this.
      THREE.MathUtils.lerp(r.from.yaw, r.to.yaw, kTurn) + CAMERA_YAW_OFFSET,
      0,
      'YXZ',
    );
    camera.quaternion.setFromEuler(euler.current);

    if (r.t >= 1) {
      run.current = null;
      if (phase === 'sitting') seatSettled();
      else standCleared();
    }
  });

  return null;
}
